import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, ProtectedRoute, useAuth } from "./Utilities/AuthContext.js";
import Login from "./LoginComponents/Login";
import OwnerLogin from "./LoginComponents/OwnerLogin";
import VerifyMagicLink from "./LoginComponents/VerifyMagicLink";
import BackgroundTheme from "./ReusableComponents/BackgroundTheme";
import Navbar from "./ReusableComponents/Navbar";
import AllPools from "./Pool/AllPools.js";
import Team from "./Team/Team.js";
import Leaderboard from "./Leaderboard/Leaderboard.js";
import AllLeaderboard from "./Leaderboard/AllLeaderboards.js";
import Dashboard from "./DashboardComponents/Dashboard.js";
import PoolPage from "./Pool/PoolPage.js";
import AllTeamsView from './Team/AllTeamsView.js';
import FAQ from './FAQ/FAQ.js';
import LoadingSpinner from "./ReusableComponents/LoadingSpinner";
import PoweredBy from "./ReusableComponents/PoweredBy";
import PublicTeamPage from "./PublicTeam/PublicTeamPage.js";
import PublicLeaderboardPage from "./PublicLeaderboard/PublicLeaderboardPage.js";

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const { isLoggedIn, isInitialized } = useAuth();

  // Public routes that don't need auth or navbar
  const isPublicRoute = React.useMemo(() => {
    const path = location.pathname;
    return path.startsWith("/t/") ||
           path.startsWith("/lb/") ||
           path.startsWith("/auth/") ||
           path === "/login" ||
           path === "/reset-password";
  }, [location.pathname]);

  const showNav = React.useMemo(() => {
    return Boolean(isLoggedIn) && !isPublicRoute;
  }, [isLoggedIn, isPublicRoute]);

  // Routes with their own full-bleed gradient/photo background render PoweredBy
  // internally so the footer inherits the page color. Everything else gets the
  // standard dark-variant footer here.
  const renderAppLevelFooter = React.useMemo(() => {
    const path = location.pathname;
    const ownsItsFooter =
      path.startsWith("/auth/") ||
      path === "/login" ||
      path === "/reset-password";
    return !ownsItsFooter;
  }, [location.pathname]);

  // Mobile bottom-nav is h-16 (64px) on authenticated routes; pad so the credit clears it.
  const poweredByPadding = showNav ? "pb-20 md:pb-2" : "pb-2";

  // Only show loading for protected routes
  if (!isInitialized && !isPublicRoute) {
    return (
      <div className="app-container loading">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="app-container">
      {showNav && <Navbar />}
      <div className={`main-content ${showNav ? 'with-navbar' : ''}`}>
        <Routes>
          {/* Public routes - no auth needed */}
          <Route path="/t/:token" element={<PublicTeamPage />} />
          <Route path="/lb/:token" element={<PublicLeaderboardPage />} />
          <Route path="/auth/login" element={<OwnerLogin />} />
          <Route path="/auth/verify" element={<VerifyMagicLink />} />

          {/* Legacy routes - keep for backwards compat */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes - owner session required */}
          <Route path="/" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <Dashboard />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/home" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <Dashboard />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/pools" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <AllPools />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/pool/:poolId" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <PoolPage />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/pool/:poolId/team/:teamId" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <Team />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/teams" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <AllTeamsView />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/leaderboard" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <AllLeaderboard />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/leaderboard/:poolId" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <Leaderboard />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
          <Route path="/faq" element={
            <ProtectedRoute>
              <BackgroundTheme>
                <FAQ />
              </BackgroundTheme>
            </ProtectedRoute>
          } />
        </Routes>
        {renderAppLevelFooter && <PoweredBy variant="dark" className={poweredByPadding} />}
      </div>
    </div>
  );
}

export default App;
